import React, { useImperativeHandle, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import SlickSlider from 'react-slick';
import useBreakpoint from 'hooks/useBreakpoint';
import styles from './Slider.module.scss';

const getSettings = (breakpoint, settings) => ({
  dots: false,
  arrows: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint,
    },
    {
      breakpoint: 9999,
      settings: 'unslick',
    },
  ],
  ...settings,
});

interface IProps {
  arrows?: boolean;
  breakpoint?: number;
  className?: string;
  dotClassName?: string;
  settings?: any;
  sliderClassName?: string;
  children: React.ReactNode;
}

const Slider = (
  {
    arrows = true,
    className,
    dotClassName,
    breakpoint = 820,
    children,
    settings,
    sliderClassName,
  }: IProps,
  ref,
) => {
  const bp = useBreakpoint(true) as number;
  const sliderRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(0);

  useImperativeHandle(ref, () => sliderRef.current);

  const handleSlideChange = (i) => sliderRef.current.slickGoTo(i);
  const handlePrevSlide = (i) => sliderRef.current.slickPrev(i);
  const handleNextSlide = (i) => sliderRef.current.slickNext(i);
  const handleBeforeChange = (_, i) => setSlideIndex(i);

  const dots = bp <= breakpoint && (
    <ul className={classnames(styles.dots, dotClassName)}>
      {arrows && (
        <ChevronLeft
          size={20}
          className={styles.prevDot}
          onClick={handlePrevSlide}
        />
      )}

      {React.Children.map(children, (_, idx) => (
        <li
          key={idx}
          className={classnames(
            idx === slideIndex && styles.active,
            idx === slideIndex && 'active',
          )}
          onClick={() => handleSlideChange(idx)}
        />
      ))}

      {arrows && (
        <ChevronRight
          size={20}
          className={styles.nextDot}
          onClick={handleNextSlide}
        />
      )}
    </ul>
  );

  return (
    <div className={classnames(styles.container, className)}>
      {dots}
      <SlickSlider
        ref={sliderRef}
        className={classnames(styles.slider, sliderClassName)}
        {...getSettings(breakpoint, settings)}
        beforeChange={handleBeforeChange}
      >
        {children}
      </SlickSlider>
      {dots}
    </div>
  );
};

export default React.forwardRef(Slider);
